import { z } from "zod";

/// Wire-shape mirrors of the shore's leave read-surface DTOs
/// (`pulsewise-shore/src/modules/leave/reports/report.types.ts` and
/// `applications/application.dto.ts`). Split across three files, matching the
/// module's own dto/types split: this one holds the caller's OWN data
/// (VIEW-1's two routes, `balances/me` and `applications/me`);
/// `leave-report.schema.ts` holds the breadth surfaces (balance report,
/// calendar, out-today); `leave-ledger.schema.ts` holds the admin-only
/// ledger.
///
/// Every day figure below is a `Decimal(9,4)` column on the shore and crosses
/// the wire as a STRING: `z.string()`, never `z.number()`. A `z.number()`
/// would coerce through a float and reintroduce the precision drift Decimal
/// exists to prevent, which is the whole reason this module reports figures
/// as strings in the first place (task-17-report.md).

export const LEAVE_APPLICATION_STATUSES = ["pending", "approved", "rejected", "cancelled"] as const;
export const leaveApplicationStatusSchema = z.enum(LEAVE_APPLICATION_STATUSES);
export type LeaveApplicationStatus = z.infer<typeof leaveApplicationStatusSchema>;

export const LEAVE_HALF_DAY_PERIODS = ["first_half", "second_half"] as const;
export const leaveHalfDayPeriodSchema = z.enum(LEAVE_HALF_DAY_PERIODS);
export type LeaveHalfDayPeriod = z.infer<typeof leaveHalfDayPeriodSchema>;

/// How many people a breadth surface (report, calendar) answers for. Decided
/// server-side from the caller's permissions; the client never sends it.
export const LEAVE_READ_SCOPES = ["own", "peers", "subtree", "workspace"] as const;
export const leaveReadScopeSchema = z.enum(LEAVE_READ_SCOPES);
export type LeaveReadScope = z.infer<typeof leaveReadScopeSchema>;

/// One person's standing on one leave type.
///
/// `credited` INCLUDES carried-forward days: it is "days credited so far",
/// never "this year's grant". Never sum it with `carried`, never show it
/// beside `available` as if it were a grant figure (fix round 1, contract
/// change: this field was named `allocated` until review caught exactly that
/// misreading).
export const leaveBalanceRowSchema = z
  .object({
    leaveTypeId: z.string(),
    leaveTypeName: z.string(),
    /// False for a type an admin retired. Landed on the shore AFTER Task 1
    /// shipped (commit 8640d92, "Tell the client which leave types are
    /// retired"), on `/balances/me` as well as the balance report: both
    /// surfaces share `LeaveBalanceRowDto`. Task 7 found the mismatch by
    /// reading the shore source rather than trusting Task 2's report, which
    /// (correctly, as of when it was written) said this field did not exist
    /// yet on `/balances/me`. Without it, every response through this
    /// `.strict()` schema now fails to parse: the extra key trips strict
    /// mode, and `apiCall` silently degrades to the raw payload. Retired
    /// types are NOT filtered out server side on purpose, so somebody still
    /// holding days on one keeps seeing the row; the client decides how to
    /// present it.
    isActive: z.boolean(),
    isCompensatory: z.boolean(),
    /// Null when no live allocation covers `asOf`; every figure is then "0".
    allocationId: z.string().nullable(),
    credited: z.string(),
    taken: z.string(),
    carried: z.string(),
    fresh: z.string(),
    /// What the person holds. The authoritative figure: credited - taken === available.
    available: z.string(),
    /// What they can still spend before days lapse: min(available, days left).
    consumable: z.string(),
    /// Days on undecided applications. Reported, never deducted.
    pending: z.string(),
    carryForwardExpiresOn: z.string().nullable(),
  })
  .strict();
export type LeaveBalanceRow = z.infer<typeof leaveBalanceRowSchema>;

/// GET .../leave/balances/me?asOf=. Self scoped by construction: no
/// membershipId parameter exists on this route.
export const myBalancesSchema = z
  .object({
    asOf: z.string(),
    membershipId: z.string(),
    /// One row per configured leave type, zero-filled. A type the caller
    /// holds nothing on still appears, so the card never has to guess "0".
    balances: z.array(leaveBalanceRowSchema),
  })
  .strict();
export type MyBalances = z.infer<typeof myBalancesSchema>;

/// One leave year, as an employee is allowed to see it. Narrower than the
/// admin `LeavePeriodDto`: no `version`, no holiday calendar id.
///
/// `name` is the admin's own label and is what the selector PRINTS. Never
/// derive a year from `fromDate` instead: a workspace on an Indian financial
/// year runs April to March and calls it "FY 2026-27", and "2026" would be
/// wrong for nine months of it.
export const myLeavePeriodSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    fromDate: z.string(),
    toDate: z.string(),
    /// The one the workspace is currently running against. At most one per
    /// workspace can hold it.
    isActive: z.boolean(),
  })
  .strict();
export type MyLeavePeriod = z.infer<typeof myLeavePeriodSchema>;

/// GET .../leave/periods/me. The leave years this person could hold leave in,
/// newest first, plus which one the page should open on.
///
/// `currentPeriodId` is null ONLY when `periods` is empty, which means an
/// admin has not created a leave period yet. It is not "no opinion": when an
/// id is present, use it.
export const myLeavePeriodsSchema = z
  .object({
    periods: z.array(myLeavePeriodSchema),
    currentPeriodId: z.string().nullable(),
  })
  .strict();
export type MyLeavePeriods = z.infer<typeof myLeavePeriodsSchema>;

/// `ApplicationDto` (`application.dto.ts`) plus `leaveTypeName`. `reason` is
/// present here and only here among the leave schemas: these are the
/// caller's OWN applications, and their own reason is not a leak.
export const myApplicationItemSchema = z
  .object({
    id: z.string(),
    membershipId: z.string(),
    leaveTypeId: z.string(),
    leaveTypeName: z.string(),
    fromDate: z.string(),
    toDate: z.string(),
    halfDay: z.boolean(),
    halfDayDate: z.string().nullable(),
    halfDayPeriod: leaveHalfDayPeriodSchema.nullable(),
    totalLeaveDays: z.string(),
    /// The balance shown at filing time. Kept so an old decision still makes
    /// sense read later, even after the allocation has moved on.
    balanceAtFiling: z.string(),
    reason: z.string(),
    contactDuringLeave: z.string().nullable(),
    status: leaveApplicationStatusSchema,
    filedByUserId: z.string(),
    decidedByUserId: z.string().nullable(),
    decidedAt: z.string().nullable(),
    decisionNote: z.string().nullable(),
    departmentId: z.string().nullable(),
    version: z.number(),
  })
  .strict();
export type MyApplicationItem = z.infer<typeof myApplicationItemSchema>;

/// GET .../leave/applications/me?status=&from=&to=&limit=&cursor=. Newest
/// leave first, cursor paged.
export const myApplicationsSchema = z
  .object({
    items: z.array(myApplicationItemSchema),
    /// Pass back as `?cursor=`. Null on the last page.
    nextCursor: z.string().nullable(),
  })
  .strict();
export type MyApplications = z.infer<typeof myApplicationsSchema>;
